import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  ClipboardList,
  BookOpen,
  MessageSquare,
  CheckCircle,
  Clock,
  Upload,
  FileText,
  PartyPopper,
  Package,
  PenLine,
  Hand,
  Check,
  Mail,
} from "lucide-react";
import { where, limit as firestoreLimit } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { format, differenceInDays, isPast } from "date-fns";
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import clsx from "clsx";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Progress } from "../../components/ui/Progress";
import { EmptyState } from "../../components/shared/EmptyState";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import {
  getDocument,
  queryDocuments,
  updateDocument,
  serverTimestamp,
} from "../../lib/firestore";
import { portalDb, portalStorage } from "../../lib/firebase";
import { usePortalSession } from "../../hooks/usePortalSession";
import type { OffboardFlow, FlowTask } from "../../types/offboarding.types";
import type { Asset } from "../../types/asset.types";
import ExitInterviewPortal from "./ExitInterviewPortal";
import KnowledgePortal from "./KnowledgePortal";
import SignatureCanvas from "../../components/ui/SignatureCanvas";
import logo from "../../assets/logo.png";

function toDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  if (typeof (ts as Timestamp).toDate === "function")
    return (ts as Timestamp).toDate();
  return null;
}

// Ensures a URL string has an absolute protocol so window.open / <a href>
// don't treat it as a relative app path (e.g. "docs.google.com/..." → app-internal 404).
function toAbsoluteUrl(url: string): string {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function OffboardSetLogo() {
  return (
    <img
      src={logo}
      alt="OffboardSet Logo"
      className="w-7 h-7 object-contain"
    />
  );
}

type PortalTab = "tasks" | "knowledge" | "interview" | "assets";

const TABS: { value: PortalTab; label: string; icon: React.ReactNode }[] = [
  { value: "tasks", label: "Tasks", icon: <ClipboardList size={16} /> },
  { value: "knowledge", label: "Knowledge", icon: <BookOpen size={16} /> },
  { value: "interview", label: "Interview", icon: <MessageSquare size={16} /> },
  { value: "assets", label: "Assets", icon: <Package size={16} /> },
];

function WelcomeHeader({
  flow,
  completedTasks,
  totalTasks,
}: {
  flow: OffboardFlow;
  completedTasks: number;
  totalTasks: number;
}) {
  const lastWorkingDay = toDate(flow.lastWorkingDay);
  const daysRemaining =
    lastWorkingDay ? differenceInDays(lastWorkingDay, new Date()) : null;
  const lastDayPassed = daysRemaining !== null && daysRemaining < 0;

  return (
    <div className="bg-white border border-navy/5 rounded-xl p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-display text-navy">
            Welcome, {flow.employeeName}
            <Hand size={18} className="text-amber" aria-hidden="true" />
          </h1>
          {lastWorkingDay && (
            <p className="text-sm text-mist mt-0.5">
              Your last day:{" "}
              <span className="font-medium text-navy">
                {format(lastWorkingDay, "MMMM d, yyyy")}
              </span>
            </p>
          )}
        </div>
        {daysRemaining !== null && (
          <span
            className={clsx(
              "text-sm font-medium whitespace-nowrap",
              lastDayPassed ? "text-ember" : "text-teal"
            )}
          >
            {lastDayPassed
              ? "Your last day has passed"
              : daysRemaining === 0
                ? "Last day is today!"
                : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining`}
          </span>
        )}
      </div>

      {totalTasks > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-mist">Task progress</span>
            <span className="text-xs font-medium text-navy">
              {completedTasks} of {totalTasks} tasks done
            </span>
          </div>
          <Progress
            value={completedTasks}
            max={totalTasks}
            size="md"
            color="teal"
          />
        </div>
      )}

      <p className="text-sm text-mist italic">
        "Thank you for your contributions. Complete these items before your last
        day to ensure a smooth exit."
      </p>
    </div>
  );
}

function AllDoneState({
  totalTasks,
  knowledgeItemCount,
  interviewCompleted,
}: {
  totalTasks: number;
  knowledgeItemCount: number;
  interviewCompleted: boolean;
}) {
  return (
    <div className="bg-white border border-navy/5 rounded-xl p-6 mb-6 text-center">
      <PartyPopper size={40} className="text-teal mx-auto mb-3" />
      <h2 className="text-xl font-display text-navy mb-2">
        You're all done!
      </h2>
      <p className="text-sm text-mist mb-5">
        Thank you for completing your exit tasks. We wish you the best in your
        next chapter.
      </p>
      <div className="space-y-2 text-left max-w-xs mx-auto mb-6">
        <div className="flex items-center gap-2 text-sm text-navy">
          <CheckCircle size={16} className="text-teal flex-shrink-0" />
          <span>All {totalTasks} tasks completed</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-navy">
          {knowledgeItemCount > 0 ? (
            <CheckCircle size={16} className="text-teal flex-shrink-0" />
          ) : (
            <Clock size={16} className="text-mist flex-shrink-0" />
          )}
          <span>
            {knowledgeItemCount > 0
              ? `${knowledgeItemCount} knowledge item${knowledgeItemCount !== 1 ? "s" : ""} submitted`
              : "No knowledge items submitted yet"}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-navy">
          {interviewCompleted ? (
            <CheckCircle size={16} className="text-teal flex-shrink-0" />
          ) : (
            <Clock size={16} className="text-mist flex-shrink-0" />
          )}
          <span>
            {interviewCompleted
              ? "Exit interview complete"
              : "Exit interview not yet completed"}
          </span>
        </div>
      </div>
      <p className="text-xs text-mist mt-2">
        Your HR team will send you an invitation to the alumni network by email.
      </p>
    </div>
  );
}

function TasksList({
  flow,
  onProgressChange,
}: {
  flow: OffboardFlow;
  onProgressChange: (completed: number, total: number) => void;
}) {
  const [tasks, setTasks] = useState<FlowTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [uploadingTaskId, setUploadingTaskId] = useState<string | null>(null);
  const [signingTaskId, setSigningTaskId] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    async function load() {
      try {
        const data = await queryDocuments<FlowTask>(
          "flowTasks",
          [
            where("flowId", "==", flow.id),
            where("assigneeRole", "==", "employee"),
          ],
          portalDb
        );
        setTasks(data);
        const completed = data.filter((t) => t.status === "completed").length;
        onProgressChange(completed, data.length);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.id]);

  async function syncFlowProgress(updatedTasks: FlowTask[]) {
    try {
      // Fetch ALL tasks (not just employee tasks) for overall progress
      const allTasks = await queryDocuments<FlowTask>(
        "flowTasks",
        [where("flowId", "==", flow.id)],
        portalDb
      );
      // Merge optimistic employee-task updates into the full set
      const taskMap = new Map(updatedTasks.map((t) => [t.id, t]));
      const merged = allTasks.map((t) => taskMap.get(t.id) ?? t);
      const completedCount = merged.filter((t) => t.status === "completed").length;
      const totalCount = merged.length;
      const progressPercent =
        totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

      await updateDocument(
        "offboardFlows",
        flow.id,
        {
          progressPercent,
          "completionScores.tasks": progressPercent,
          ...(flow.status === "not_started" ? { status: "in_progress" } : {}),
        },
        portalDb
      );
    } catch {
      // Silent fail — progress sync is best-effort
    }
  }

  async function handleToggleTask(task: FlowTask) {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    const prevTasks = tasks;

    // Optimistic update
    const updatedTasks = tasks.map((t) =>
      t.id === task.id
        ? {
            ...t,
            status: newStatus,
            completedAt:
              newStatus === "completed" ? Timestamp.now() : null,
            completedBy: newStatus === "completed" ? "employee" : "",
          }
        : t
    ) as FlowTask[];
    setTasks(updatedTasks);
    onProgressChange(
      updatedTasks.filter((t) => t.status === "completed").length,
      updatedTasks.length
    );

    try {
      setSaveError(null);
      await updateDocument(
        "flowTasks",
        task.id,
        {
          status: newStatus,
          completedAt: newStatus === "completed" ? serverTimestamp() : null,
          completedBy: newStatus === "completed" ? "employee" : "",
        },
        portalDb
      );
      await syncFlowProgress(updatedTasks);
    } catch {
      setTasks(prevTasks);
      onProgressChange(
        prevTasks.filter((t) => t.status === "completed").length,
        prevTasks.length
      );
      setSaveError("Failed to save task. Please try again.");
    }
  }

  async function handleSignatureSave(
    task: FlowTask,
    signatureDataUrl: string
  ) {
    try {
      const fileName = `${task.id}-signature-${Date.now()}.png`;
      const filePath = `companies/${flow.companyId}/offboardings/${flow.id}/tasks/${task.id}/${fileName}`;
      const storageRef = ref(portalStorage, filePath);

      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      const snapshot = await uploadBytes(storageRef, blob, {
        contentType: "image/png",
      });
      const downloadURL = await getDownloadURL(snapshot.ref);
      await updateDocument(
        "flowTasks",
        task.id,
        {
          status: "completed",
          completedAt: serverTimestamp(),
          completedBy: "employee",
          uploadedFileUrl: downloadURL,
        },
        portalDb
      );
      const updatedTasks = tasks.map((t) =>
        t.id === task.id
          ? {
              ...t,
              status: "completed" as const,
              uploadedFileUrl: downloadURL,
            }
          : t
      );
      setTasks(updatedTasks);
      setSigningTaskId(null);
      onProgressChange(
        updatedTasks.filter((t) => t.status === "completed").length,
        updatedTasks.length
      );
      await syncFlowProgress(updatedTasks);
    } catch {
      setSaveError("Failed to save signature. Please try again.");
    }
  }

  async function handleFileUpload(task: FlowTask, file: File) {
    setUploadingTaskId(task.id);
    setUploadProgress((prev) => ({ ...prev, [task.id]: 0 }));

    try {
      // Some browsers return an empty MIME type for valid file types (e.g. .docx on iOS).
      // Derive from extension as a fallback so storage rules aren't rejected.
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      const MIME_FALLBACKS: Record<string, string> = {
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        xls: "application/vnd.ms-excel",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ppt: "application/vnd.ms-powerpoint",
        pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        gif: "image/gif",
        webp: "image/webp",
        txt: "text/plain",
      };
      const contentType = file.type || MIME_FALLBACKS[ext] || "application/octet-stream";

      const filePath = `companies/${flow.companyId}/offboardings/${flow.id}/tasks/${task.id}/${file.name}`;
      const storageRef = ref(portalStorage, filePath);
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType,
      });

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const pct = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setUploadProgress((prev) => ({ ...prev, [task.id]: pct }));
          },
          reject,
          async () => {
            try {
              const downloadURL = await getDownloadURL(
                uploadTask.snapshot.ref
              );
              await updateDocument(
                "flowTasks",
                task.id,
                {
                  status: "completed",
                  completedAt: serverTimestamp(),
                  completedBy: "employee",
                  uploadedFileUrl: downloadURL,
                },
                portalDb
              );
              const updatedTasks = tasks.map((t) =>
                t.id === task.id
                  ? {
                      ...t,
                      status: "completed" as const,
                      uploadedFileUrl: downloadURL,
                    }
                  : t
              );
              setTasks(updatedTasks);
              onProgressChange(
                updatedTasks.filter((t) => t.status === "completed").length,
                updatedTasks.length
              );
              await syncFlowProgress(updatedTasks);
              resolve();
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    } catch {
      setSaveError("Upload failed. Please try again.");
    } finally {
      setUploadingTaskId(null);
    }
  }

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No tasks assigned"
          description="You don't have any tasks to complete for your offboarding."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {saveError && (
        <div className="px-4 py-3 bg-ember/10 border border-ember/20 rounded-lg text-sm text-ember">
          {saveError}
        </div>
      )}
      {tasks.map((task) => {
        const dueDate = toDate(task.dueDate);
        const overdue =
          dueDate &&
          isPast(dueDate) &&
          task.status !== "completed" &&
          task.status !== "skipped";
        const isUploading = uploadingTaskId === task.id;
        const uploadPct = uploadProgress[task.id] ?? 0;
        const isUploadTask = task.type === "upload";
        const isSignatureTask = task.type === "signature";
        const isClickable = !isUploadTask && !isSignatureTask;

        const dependsOnTask = task.dependsOnTaskId
          ? tasks.find((t) => t.id === task.dependsOnTaskId)
          : null;
        const dependencyMet =
          !dependsOnTask || dependsOnTask.status === "completed";
        const isLocked = !dependencyMet;

        return (
          <div
            key={task.id}
            className="bg-white border border-navy/5 rounded-lg overflow-hidden"
          >
            <div className="flex items-start gap-3 px-4 py-3">
              {/* Checkbox — clickable for non-upload tasks */}
              {isClickable ? (
                <button
                  onClick={() => !isLocked && handleToggleTask(task)}
                  aria-label={
                    task.status === "completed"
                      ? "Mark as pending"
                      : "Mark as completed"
                  }
                  disabled={isLocked}
                  className={clsx(
                    "mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                    isLocked && "opacity-50 cursor-not-allowed",
                    task.status === "completed"
                      ? "bg-teal border-teal text-white"
                      : "border-navy/20 hover:border-teal"
                  )}
                >
                  {task.status === "completed" && (
                    <svg
                      width="10"
                      height="8"
                      viewBox="0 0 10 8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 4l3 3 5-6" />
                    </svg>
                  )}
                </button>
              ) : (
                <div
                  className={clsx(
                    "mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0",
                    task.status === "completed"
                      ? "bg-teal border-teal text-white"
                      : "border-navy/20"
                  )}
                >
                  {task.status === "completed" ? (
                    <svg
                      width="10"
                      height="8"
                      viewBox="0 0 10 8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 4l3 3 5-6" />
                    </svg>
                  ) : (
                    <Upload size={10} className="text-mist" />
                  )}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p
                  className={clsx(
                    "text-sm font-medium",
                    task.status === "completed"
                      ? "text-mist line-through"
                      : "text-navy"
                  )}
                >
                  {task.title}
                </p>
                {task.description && (
                  <p className="text-xs text-mist mt-0.5">{task.description}</p>
                )}
                {isLocked && dependsOnTask && (
                  <p className="text-xs text-amber-600 mt-1">
                    Unlocks after: {dependsOnTask.title}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {dueDate && (
                  <span
                    className={clsx(
                      "text-xs",
                      overdue ? "text-ember font-medium" : "text-mist"
                    )}
                  >
                    {format(dueDate, "MMM d")}
                  </span>
                )}
                <Badge
                  variant={
                    task.status === "completed"
                      ? "teal"
                      : task.status === "overdue"
                        ? "ember"
                        : "mist"
                  }
                >
                  {task.status === "completed"
                    ? "Done"
                    : task.status === "overdue"
                      ? "Overdue"
                      : "Pending"}
                </Badge>
              </div>
            </div>

            {/* Signature area for signature tasks */}
            {task.type === "signature" && (
              <div className="px-4 pb-3">
                {task.status === "completed" && task.uploadedFileUrl ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-teal flex-shrink-0" />
                    <span className="text-xs text-navy">Signature captured</span>
                    <button
                      onClick={() => setSigningTaskId(task.id)}
                      className="text-xs text-teal underline flex-shrink-0"
                    >
                      Re-sign
                    </button>
                  </div>
                ) : signingTaskId === task.id ? (
                  <SignatureCanvas
                    onSignatureSave={(sig) => handleSignatureSave(task, sig)}
                    onCancel={() => setSigningTaskId(null)}
                  />
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setSigningTaskId(task.id)}
                    className="w-full inline-flex items-center justify-center gap-1.5"
                  >
                    <PenLine size={14} />
                    Draw Signature
                  </Button>
                )}
              </div>
            )}

            {/* Form/Link tasks */}
            {(task.type === "form" || task.type === "link") && (
              <div className="px-4 pb-3">
                {task.status === "completed" ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-teal flex-shrink-0" />
                      <span className="text-xs text-navy">
                        {task.type === "form" ? "Form completed" : "Link accessed"}
                      </span>
                    </div>
                    {task.linkUrl && (
                      <a
                        href={toAbsoluteUrl(task.linkUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-teal underline"
                      >
                        Open again
                      </a>
                    )}
                  </div>
                ) : (
                  task.linkUrl ? (
                    <a
                      href={toAbsoluteUrl(task.linkUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-teal text-white hover:bg-teal-light shadow-sm transition-colors"
                    >
                      {task.type === "form" ? "Open Form" : "Open Link"}
                    </a>
                  ) : (
                    <span className="text-xs text-mist">No link provided</span>
                  )
                )}
              </div>
            )}

            {/* File upload area for upload tasks */}
            {isUploadTask && (
              <div className="px-4 pb-3">
                {task.status === "completed" && task.uploadedFileUrl ? (
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-teal flex-shrink-0" />
                    <span className="text-xs text-navy truncate flex-1">
                      {decodeURIComponent(
                        task.uploadedFileUrl
                          .split("/")
                          .pop()
                          ?.split("?")[0] ?? "Uploaded file"
                      )}
                    </span>
                    <button
                      onClick={() =>
                        fileInputRefs.current[task.id]?.click()
                      }
                      className="text-xs text-teal underline flex-shrink-0"
                    >
                      Re-upload
                    </button>
                    <input
                      ref={(el) => {
                        fileInputRefs.current[task.id] = el;
                      }}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(task, file);
                        e.target.value = "";
                      }}
                    />
                  </div>
                ) : isUploading ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-mist">
                      <span>Uploading…</span>
                      <span>{uploadPct}%</span>
                    </div>
                    <Progress
                      value={uploadPct}
                      max={100}
                      size="sm"
                      color="teal"
                    />
                  </div>
                ) : (
                  <>
                    <input
                      ref={(el) => {
                        fileInputRefs.current[task.id] = el;
                      }}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(task, file);
                        e.target.value = "";
                      }}
                    />
                    <button
                      onClick={() =>
                        fileInputRefs.current[task.id]?.click()
                      }
                      className="w-full border-2 border-dashed border-navy/15 rounded-lg py-3 px-4 text-center hover:border-teal/50 hover:bg-teal/5 transition-colors"
                    >
                      <Upload size={16} className="text-mist mx-auto mb-1" />
                      <p className="text-xs text-mist">
                        Click to upload a file
                      </p>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AssetsList({ flow }: { flow: OffboardFlow }) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [pendingNotes, setPendingNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    queryDocuments<Asset>("assets", [where("flowId", "==", flow.id)], portalDb)
      .then((data) => setAssets(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [flow.id]);

  async function handleConfirmReturned(asset: Asset) {
    setUpdatingId(asset.id);
    const note = pendingNotes[asset.id]?.trim() || "";
    try {
      await updateDocument(
        "assets",
        asset.id,
        {
          status: "returned",
          returnedAt: serverTimestamp(),
          ...(note ? { notes: note } : {}),
        },
        portalDb
      );
      setAssets((prev) =>
        prev.map((a) =>
          a.id === asset.id
            ? { ...a, status: "returned" as Asset["status"], notes: note || a.notes }
            : a,
        ),
      );
      setPendingNotes((prev) => { const n = { ...prev }; delete n[asset.id]; return n; });
    } catch {
      // ignore
    } finally {
      setUpdatingId(null);
    }
  }

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No assets to return"
          description="Your HR team hasn't assigned any assets for return. Check back later or contact HR."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-mist">
        The following company assets are assigned to you. Return each item before your last day.
      </p>

      {/* 2-step legend */}
      <div className="flex items-center gap-6 px-4 py-3 bg-navy/5 rounded-lg text-xs text-mist">
        <div className="flex items-center gap-1.5">
          <span className="h-5 w-5 rounded-full bg-teal flex items-center justify-center text-white font-bold text-[10px]">1</span>
          <span>You confirm return</span>
        </div>
        <div className="h-px flex-1 bg-navy/10" />
        <div className="flex items-center gap-1.5">
          <span className="h-5 w-5 rounded-full bg-teal flex items-center justify-center text-white font-bold text-[10px]">2</span>
          <span>HR / IT verifies</span>
        </div>
      </div>

      {assets.map((asset) => {
        const employeeStep = asset.status === "assigned";
        const adminStep = asset.status === "returned";
        const done = asset.status === "verified" || asset.status === "wiped";

        return (
          <div key={asset.id} className="bg-white border border-navy/5 rounded-lg overflow-hidden">
            {/* Asset header */}
            <div className="flex items-start gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-navy">{asset.name}</p>
                <p className="text-xs text-mist mt-0.5">
                  {asset.type}{asset.serialNumber ? ` · SN: ${asset.serialNumber}` : ""}
                </p>
                {asset.notes && !employeeStep && (
                  <p className="text-xs text-navy/60 mt-1 italic">Note: {asset.notes}</p>
                )}
              </div>
              <Badge
                variant={done ? "teal" : adminStep ? "amber" : "mist"}
              >
                {done ? "Complete" : adminStep ? "Pending verification" : "Awaiting return"}
              </Badge>
            </div>

            {/* Progress stepper */}
            <div className="border-t border-navy/5 px-4 py-3">
              <div className="flex items-center gap-2">
                {/* Step 1 */}
                <div className={clsx(
                  "flex items-center gap-1.5 text-xs font-medium",
                  !employeeStep ? "text-teal" : "text-mist"
                )}>
                  <span className={clsx(
                    "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0",
                    !employeeStep ? "bg-teal text-white" : "bg-navy/10 text-navy"
                  )}>
                    {!employeeStep ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : "1"}
                  </span>
                  You confirmed
                </div>
                <div className={clsx("flex-1 h-px", !employeeStep ? "bg-teal" : "bg-navy/10")} />
                {/* Step 2 */}
                <div className={clsx(
                  "flex items-center gap-1.5 text-xs font-medium",
                  done ? "text-teal" : "text-mist"
                )}>
                  <span className={clsx(
                    "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0",
                    done ? "bg-teal text-white" : "bg-navy/10 text-navy"
                  )}>
                    {done ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : "2"}
                  </span>
                  HR / IT verified
                </div>
              </div>

              {/* Action */}
              {employeeStep && (
                <div className="mt-3 space-y-2">
                  <textarea
                    rows={2}
                    placeholder="Add a note (optional) — e.g. charger not included, minor scratch on lid…"
                    value={pendingNotes[asset.id] ?? ""}
                    onChange={(e) =>
                      setPendingNotes((prev) => ({ ...prev, [asset.id]: e.target.value }))
                    }
                    className="w-full rounded-md border border-navy/20 px-3 py-2 text-sm text-navy placeholder:text-mist focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal resize-none"
                  />
                  <button
                    type="button"
                    disabled={updatingId === asset.id}
                    onClick={() => handleConfirmReturned(asset)}
                    className={clsx(
                      "w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md border-2 border-dashed text-sm font-medium transition-colors",
                      updatingId === asset.id
                        ? "opacity-50 cursor-not-allowed border-navy/10 text-mist"
                        : "border-teal/40 text-teal hover:bg-teal/5 cursor-pointer"
                    )}
                  >
                    <CheckCircle size={15} />
                    {updatingId === asset.id ? "Confirming…" : "Confirm I have returned this asset"}
                  </button>
                </div>
              )}
              {adminStep && (
                <p className="mt-2 text-xs text-amber-600 text-center">
                  Return received — waiting for HR / IT to verify
                </p>
              )}
              {done && (
                <p className="mt-2 text-xs text-teal text-center">
                  This asset has been verified. Thank you!
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PortalEntry() {
  const { token } = useParams<{ token: string }>();
  // The link token is redeemed server-side for a flow-scoped auth session.
  // Until that resolves the portal holds no Firestore access at all.
  const portalSession = usePortalSession(token);
  const [flow, setFlow] = useState<OffboardFlow | null>(null);
  const [flowLoading, setFlowLoading] = useState(true);
  const [flowError, setFlowError] = useState("");
  const [portalTab, setPortalTab] = useState<PortalTab>("tasks");
  const [completedTasks, setCompletedTasks] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);
  const [knowledgeItemCount, setKnowledgeItemCount] = useState(0);
  const [interviewCompleted, setInterviewCompleted] = useState(false);

  const flowId = portalSession.session?.flowId;

  useEffect(() => {
    if (!flowId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const found = await getDocument<OffboardFlow>(
          "offboardFlows",
          flowId,
          portalDb
        );
        if (cancelled) return;
        if (!found) {
          setFlowError("This portal link is invalid or has expired.");
        } else {
          setFlow(found);
        }
      } catch {
        if (!cancelled) {
          setFlowError("Something went wrong. Please try again later.");
        }
      } finally {
        if (!cancelled) setFlowLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  useEffect(() => {
    if (!flow) return;
    const loadCompletionStatus = async () => {
      try {
        const [knowledgeItems, interviews] = await Promise.all([
          queryDocuments(
            "knowledgeItems",
            [where("flowId", "==", flow.id)],
            portalDb
          ),
          queryDocuments(
            "exitInterviewResponses",
            [where("flowId", "==", flow.id), firestoreLimit(1)],
            portalDb
          ),
        ]);
        setKnowledgeItemCount(knowledgeItems.length);
        setInterviewCompleted(interviews.length > 0);
      } catch (err) {
        console.error("PortalEntry completion status error:", err);
      }
    };
    loadCompletionStatus();
  }, [flow]);

  if (portalSession.status === "loading" || (flowLoading && !flowError)) {
    return (
      <div className="min-h-screen bg-warm/30 flex items-center justify-center">
        <div className="text-sm text-mist">Loading portal…</div>
      </div>
    );
  }

  const fatalError =
    portalSession.status === "error" ? portalSession.error : flowError;

  if (fatalError || !flow) {
    return (
      <div className="min-h-screen bg-warm/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <EmptyState
            title="Portal Unavailable"
            description={fatalError || "This portal link is invalid."}
          />
        </Card>
      </div>
    );
  }

  // Check cancelled status
  if (flow.status === "cancelled") {
    return (
      <div className="min-h-screen bg-warm/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <EmptyState
            title="Portal Closed"
            description="This exit portal has closed. If you need access, contact your HR team for assistance."
            action={
              <a href="mailto:hr@yourcompany.com">
                <Button variant="outline" size="sm">
                  <Mail size={14} className="mr-1.5" aria-hidden="true" />
                  Contact HR
                </Button>
              </a>
            }
          />
        </Card>
      </div>
    );
  }

  // Check link expiry — 7 days after last working day
  const lastWorkingDay = toDate(flow.lastWorkingDay);
  if (lastWorkingDay) {
    const expiryDate = new Date(lastWorkingDay);
    expiryDate.setDate(expiryDate.getDate() + 7);
    if (new Date() > expiryDate) {
      return (
        <div className="min-h-screen bg-warm/30 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <EmptyState
              title="Portal Closed"
              description="This exit portal has closed. If you need access, contact your HR team for assistance."
              action={
                <a href="mailto:hr@yourcompany.com">
                  <Button variant="outline" size="sm">
                    <Mail size={14} className="mr-1.5" aria-hidden="true" />
                    Contact HR
                  </Button>
                </a>
              }
            />
          </Card>
        </div>
      );
    }
  }

  const allTasksDone = totalTasks > 0 && completedTasks === totalTasks;

  return (
    <div className="min-h-screen bg-warm/30">
      {/* Top bar */}
      <div className="bg-white border-b border-navy/10 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <OffboardSetLogo />
            <span className="font-display text-navy text-lg">OffboardSet</span>
          </div>
          <span className="text-sm text-mist">{flow.employeeName}</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        {/* Welcome header — visible on all tabs */}
        <WelcomeHeader
          flow={flow}
          completedTasks={completedTasks}
          totalTasks={totalTasks}
        />

        {/* Tasks tab */}
        <div className={portalTab === "tasks" ? "block" : "hidden"}>
          {allTasksDone && (
            <AllDoneState
              totalTasks={totalTasks}
              knowledgeItemCount={knowledgeItemCount}
              interviewCompleted={interviewCompleted}
            />
          )}
          <h2 className="text-base font-semibold text-navy mb-3">
            {allTasksDone ? "Completed Tasks" : "Your Tasks"}
          </h2>
          {/* Always mounted so progress data stays up to date */}
          <TasksList
            flow={flow}
            onProgressChange={(completed, total) => {
              setCompletedTasks(completed);
              setTotalTasks(total);
            }}
          />
        </div>

        {portalTab === "knowledge" && <KnowledgePortal flow={flow} />}

        {portalTab === "interview" && (
          <>
            <h2 className="text-base font-semibold text-navy mb-3">
              Exit Interview
            </h2>
            <ExitInterviewPortal flow={flow} />
          </>
        )}

        {portalTab === "assets" && (
          <>
            <h2 className="text-base font-semibold text-navy mb-3">
              Assets to Return
            </h2>
            <AssetsList flow={flow} />
          </>
        )}
      </div>

      {/* Bottom tab bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-navy/10">
        <div className="max-w-2xl mx-auto flex">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setPortalTab(tab.value)}
              className={clsx(
                "flex-1 py-3 text-sm font-medium flex flex-col items-center gap-1 transition-colors",
                portalTab === tab.value
                  ? "text-teal"
                  : "text-mist hover:text-navy"
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
